import type { ColorString } from "highcharts/highcharts";

import type { VennDataItem, VennDiagramData } from "@similarweb/pro-logic-components";

import { translate } from "app/filters/i18nFilter";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";

import { COLORS_CONFIG, VENN_COLOR_KEY_DELIMITER } from "../const";
import type { WebsiteItem } from "../inline-website-query-bar/types";
import type { IVennDiagramTransformedItem, IKeywordsGapServerResponse } from "../Types";

export const getSiteKey = (site: WebsiteItem): string => {
    if (site.searchType === "folder") {
        return `${site.url}/`;
    }
    return site.url;
};

export const combineSiteFilters = (pageFilterJson: string, competitorsFilterJson: string): WebsiteItem[] => {
    const pageFilter = safeJsonParse(pageFilterJson, []) as WebsiteItem[];
    const competitorsFilter = safeJsonParse(competitorsFilterJson, []) as WebsiteItem[];
    return [...pageFilter, ...competitorsFilter];
};

export const getConcatenatedSiteKeys = (primaryKey: string, competitorsFilter: string): string[] => {
    const competitors = safeJsonParse(competitorsFilter, []) as { url: string }[];
    const competitorKeys = competitors.map((competitor) => competitor.url.split("/")[0]);
    return [...new Set([...competitorKeys, primaryKey])];
};

export const transformKeywordsGapData = (
    response: IKeywordsGapServerResponse,
    keys: string[],
): IVennDiagramTransformedItem[] => {
    const hasData = response.keywordGaps.some((gap) => gap.keywordCount > 0);
    if (!hasData) {
        return [];
    }
    const competitorIndices: Record<string, number> = keys.reduce((acc, key, index) => {
        return { ...acc, [key]: index };
    }, {});

    return response.keywordGaps.map((gap) => {
        const sets = gap.competitors;
        const id = sets.join(VENN_COLOR_KEY_DELIMITER);
        const isIntersection = sets.length > 1;

        if (isIntersection) {
            const setIndices = sets.map((set) => competitorIndices[set]).sort();
            const indexKey = setIndices.join(VENN_COLOR_KEY_DELIMITER);
            const intersectionColor: string = COLORS_CONFIG.intersection.get(indexKey);

            return {
                id,
                sets,
                name: id,
                visible: true,
                keywordsValue: gap.keywordCount,
                volumeValue: gap.volume,
                hideInLegend: true,
                color: intersectionColor,
                borderColor: intersectionColor,
                opacity: 1,
            };
        } else {
            const competitorIndex = competitorIndices[sets[0]];

            return {
                id,
                sets,
                name: id,
                visible: true,
                keywordsValue: gap.keywordCount,
                volumeValue: gap.volume,
                hideInLegend: false,
                color: COLORS_CONFIG.single[competitorIndex],
                borderColor: COLORS_CONFIG.border[competitorIndex],
            };
        }
    }) as IVennDiagramTransformedItem[];
};

export const legendDataAdapter = (vennDiagramData: VennDiagramData): VennDataItem[] =>
    vennDiagramData[0].data
        .filter((item) => !item.hideInLegend)
        .map((legend) => {
            const color: ColorString = legend.borderColor as ColorString;
            return { ...legend, color };
        });

export const getNoDataTitle = (hasCompetitors: boolean, isError: boolean) => {
    if (!hasCompetitors) {
        return {
            noDataTitle: translate("wa.traffic.sources.search.seo.overview.keywords.gap.no_data.no_competitors"),
            noDataSubtitle: translate(
                "wa.traffic.sources.search.seo.overview.keywords.gap.no_data_subtitle.no_competitors",
            ),
        };
    } else if (isError) {
        return {
            noDataTitle: translate("wa.traffic.sources.search.seo.overview.keywords.gap.no_data.error"),
            noDataSubtitle: translate("wa.traffic.sources.search.seo.overview.keywords.gap.no_data_subtitle.error"),
        };
    }

    return {
        noDataTitle: translate("wa.traffic.sources.search.seo.overview.keywords.gap.no_data"),
        noDataSubtitle: translate("wa.traffic.sources.search.seo.overview.keywords.gap.no_data_subtitle"),
    };
};
